/// <amd-module name="TFS/Admin/Views/AdminHub/AdminHub" />
import React = require("react");

import { AdminHubBreadcrumbService } from "TFS/Admin/Views/AdminHub/AdminHubBreadcrumbService";

import AdminResources = require("TFS/Admin/Views/AdminHub/Resources");
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, ObservableCollection, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { addTabs, ILegacyContributedTab, processTabContribution } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled, isFeatureEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssPageService } from "VSS/Platform/Page";

import { Button } from "VSSUI/Button";
import { IIconProps } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Orientation, Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Splitter, SplitterDirection, SplitterElementPosition } from "VSSUI/Splitter";
import { IStackPanelItem, StackPanel, StackPanelProvider } from "VSSUI/StackPanel";
import { ITabGroup as VssUiTabGroup, IVssContributedTab, TabContent, TabList } from "VSSUI/Tabs";
import { TextField, TextFieldFocusTreatmentBehavior, TextFieldStyle, TextFieldWidth } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { ISettingsSearchComponentProps } from "TFS/Admin/Views/SettingsSearch/admin-settings-search.Props";

interface IAdminHubComponentProps {
    /** We will load pivots (tabs) that target tab groups that target this id */
    id: string;
    level: "project" | "collection" | "user";
    /** Data provider with data about legacy hub contributions */
    hubContributionDataProvider?: string;
    /** Id of the default pivot */
    defaultPivot?: string;
    displayTabIcons?: boolean;
}

interface ITabGroup {
    id: string;
    name: string;
    rank: number;
}

interface IContributionData {
    contributions: {
        id: string;
        properties: ILegacyContributedTab;
    }[];
}

class AdminHubComponent extends VssComponent<IAdminHubComponentProps, {}> {
    private static readonly AdminHubSplitterEnabledFeatureFlag = "VisualStudio.Services.AdminEngagement.AdminHub.EnableSplitterLayout";
    private static readonly AdminHubSettingsSearchEnabledFeatureId = "ms.vss-admin-web.organization-admin-settings-search-feature";
    private static readonly ContributedGroupKey = "contributed";
    private static readonly PivotParamName = "adminPivot";

    private adminBreadcrumbServie: AdminHubBreadcrumbService;
    private contributionMap: { [id: string]: IVssContributedTab };
    /** the name of the project/account/collection */
    private entityName: string | undefined;
    private groups: VssUiTabGroup[];
    private isSplitterEnabled: boolean;
    /** e.g. "Account Settings" or "Project Settings" or "Collection Settings" */
    private name: string;

    private selectedTabId: ObservableValue<string>;
    private stackPanel: StackPanel;
    private tabListPushItem: IStackPanelItem;
    private tabProviders: IReadonlyObservableArray<IVssContributedTab>;

    private searchEnabled: ObservableValue<boolean>;
    private searchValue = new ObservableValue("");
    private textField = React.createRef<TextField>();
    private isSearchFlagEnabled: boolean;

    constructor(props: IAdminHubComponentProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
        this.searchEnabled = new ObservableValue(false);

        if (props.level === "collection") {
            const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
            const vssPageData = vssPageService.getData();

            if (vssPageData) {
                this.entityName = vssPageData.hostName;

                if (vssPageData && vssPageData.isHosted) {
                    this.name = AdminResources.AccountSettings;
                } else {
                    this.name = AdminResources.CollectionSettings;
                }
            }
        } else if (props.level === "project") {
            this.name = AdminResources.ProjectSettings;

            const tfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            if (tfsPageData && tfsPageData.project) {
                this.entityName = tfsPageData.project.name;
            }
        } else if (props.level === "user") {
            this.name = AdminResources.UserSettings;

            const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
            const vssPageData = vssPageService.getData();

            if (vssPageData && vssPageData.user) {
                this.entityName = vssPageData.user.displayName;
            }
        }

        this.selectedTabId = new ObservableValue<string>(this.getCurrentTab());

        this.contributionMap = {};

        this.adminBreadcrumbServie = context.pageContext.getService<AdminHubBreadcrumbService>("admin-hub-header-breadcrumb-service");

        this.initialize();
    }

    public render(): JSX.Element {
        return (
            <Page className="admin-hub" tabProviders={this.tabProviders} selectedTabId={this.selectedTabId} orientation={Orientation.Horizontal}>
                <Observer searchEnabled={this.searchEnabled}>
                    {(props: { searchEnabled: boolean }) => (
                        <>
                            {this.isSplitterEnabled ? (
                                <Splitter
                                    fixedElement={SplitterElementPosition.Near}
                                    splitterDirection={SplitterDirection.Vertical}
                                    minFixedSize={240}
                                    maxFixedSize={500}
                                    initialFixedSize={240}
                                    nearElementClassName="flex flex-column v-scroll-auto"
                                    onRenderNearElement={() => {
                                        return (
                                            <div>
                                                {this.name && this.renderTitle(this.name)}
                                                {this.entityName && this.renderSubTitle(this.entityName)}
                                                {this.isSearchFlagEnabled && this.props.level === "collection" && this._showSeachTextField}
                                                <StackPanel
                                                    initialStack={[this.tabListPushItem]}
                                                    ref={(stackPanel: StackPanel) => (this.stackPanel = stackPanel)}
                                                />
                                            </div>
                                        );
                                    }}
                                    farElementClassName="flex flex-column"
                                    onRenderFarElement={() => {
                                        return props.searchEnabled ? (
                                            <WrappedComponent<ISettingsSearchComponentProps>
                                                dependencies={["ms.vss-admin-web.settings-search-component"]}
                                                wrappedType={"ms.vss-admin-web.setting-search-page"}
                                                wrappedProps={{
                                                    pageContext: this.context.pageContext,
                                                    searchTerm: this.searchValue.value
                                                }}
                                            />
                                        ) : (
                                            <StackPanelProvider push={this.push} pop={this.pop} peek={this.peek}>
                                                <TabContent />
                                            </StackPanelProvider>
                                        );
                                    }}
                                />
                            ) : (
                                <>
                                    <div className="legacy-stackpanel-div flex-noshrink">
                                        {this.name && this.renderTitle(this.name)}
                                        {this.entityName && this.renderSubTitle(this.entityName)}
                                        {this.isSearchFlagEnabled && this.props.level === "collection" && this._showSeachTextField}
                                        <StackPanel
                                            initialStack={[this.tabListPushItem]}
                                            ref={(stackPanel: StackPanel) => (this.stackPanel = stackPanel)}
                                            className={"legacy-stackpanel"}
                                        />
                                    </div>
                                    <div className="flex-column flex-grow v-scroll-auto relative">
                                        {props.searchEnabled ? (
                                            <WrappedComponent<ISettingsSearchComponentProps>
                                                dependencies={["ms.vss-admin-web.settings-search-component"]}
                                                wrappedType={"ms.vss-admin-web.setting-search-page"}
                                                wrappedProps={{
                                                    pageContext: this.context.pageContext,
                                                    searchTerm: this.searchValue.value
                                                }}
                                            />
                                        ) : (
                                            <StackPanelProvider push={this.push} pop={this.pop} peek={this.peek}>
                                                <TabContent />
                                            </StackPanelProvider>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Observer>
            </Page>
        );
    }

    public componentDidMount() {
        super.componentDidMount();
        this.updatePageTitleAndBreadcrumb(this.selectedTabId.value);
    }

    private onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        switch (ev.which) {
            case KeyCode.enter:
                this.searchEnabled.value = true;
                break;

            case KeyCode.escape:
                this.searchEnabled.value = false;
                break;

            default:
                return;
        }

        ev.preventDefault();
        ev.stopPropagation();
    };

    private onTextChanged = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        this.searchValue.value = value;
    };

    private onClickClearButton = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        this.searchValue.value = "";
        if (this.textField.current) {
            this.textField.current.focus();
        }
    };

    private clearButtonIconProps: IIconProps = {
        render: () => {
            if (this.searchValue.value) {
                return (
                    <Button
                        className="bolt-text-filterbaritem-clear search-clear-button"
                        iconProps={{ iconName: "Cancel" }}
                        ariaLabel={AdminResources.CancelButton}
                        onClick={this.onClickClearButton}
                    />
                );
            } else {
                return <div />;
            }
        }
    };

    private _showSeachTextField = (
        <div className="setting-search-textbox">
            <TextField
                ariaLabel={AdminResources.SearchSettings}
                className="bolt-text-filterbaritem flex-grow"
                inputClassName="search-text-field-input"
                onChange={this.onTextChanged}
                width={TextFieldWidth.auto}
                onKeyDown={this.onKeyDown}
                maxLength={50}
                placeholder={AdminResources.SearchSettings}
                ref={this.textField}
                style={TextFieldStyle.inline}
                suffixIconProps={this.clearButtonIconProps}
                value={this.searchValue}
                prefixIconProps={{ className: "keyword-filter-icon", iconName: "Search" }}
            />
        </div>
    );

    private getCurrentTab(): string {
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        return navigationService.getRouteValues()[AdminHubComponent.PivotParamName] || this.props.defaultPivot || "";
    }

    private initialize(): void {
        const loadingComponent = () => (
            <div className="absolute-fill flex flex-column flex-center justify-center">
                <Spinner size={SpinnerSize.large} />
            </div>
        );

        // find the tab groups that target this component
        const providerCollection = new ObservableCollection<IVssContributedTab>();
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const tabGroupIds = contributionService.getContributionChildren(this.props.id, "ms.vss-web.tab-group") || [];

        this.groups = [];
        tabGroupIds.forEach(tgId => {
            addTabs(this.context.pageContext, providerCollection, tgId, {
                updateUrlOnPivotChange: true,
                routeStateKey: AdminHubComponent.PivotParamName,
                fpsRequired: true,
                preProcessTabContribution: (tab: IVssContributedTab | ILegacyContributedTab): void => {
                    this.preProcessContributedTab(tab, tgId);
                }
            });
            const tg = contributionService.getContribution<ITabGroup>(tgId);
            if (tg) {
                this.groups.push({ id: tgId, name: tg.name, order: tg.rank });
            }
        });

        this.groups.push({
            id: AdminHubComponent.ContributedGroupKey,
            name: AdminResources.Extensions,
            order: 999
        });

        if (this.props.hubContributionDataProvider) {
            const contributionData = contributionService.getData<IContributionData>(this.props.hubContributionDataProvider);
            if (contributionData) {
                providerCollection.push(contributionData.contributions, c =>
                    processTabContribution(
                        this.context.pageContext,
                        {
                            updateUrlOnPivotChange: true,
                            routeStateKey: AdminHubComponent.PivotParamName,
                            fpsRequired: true,
                            loadingComponent: loadingComponent,
                            preProcessTabContribution: (tab: IVssContributedTab | ILegacyContributedTab): void => {
                                this.preProcessContributedTab(tab, AdminHubComponent.ContributedGroupKey);
                            }
                        },
                        c.properties,
                        c.id
                    )
                );
            }
        }

        providerCollection.value.forEach(tab => {
            this.contributionMap[tab.id] = tab;
        });

        // if tab doesn't exists set
        if (!this.contributionMap[this.selectedTabId.value]) {
            this.selectedTabId.value = providerCollection.value[0].id;
        }

        this.tabProviders = providerCollection;

        this.tabListPushItem = {
            renderContent: () => <TabList className="admin-hub-tablist" tabGroups={this.groups} onSelectedTabChanged={this.onSelectedTabChanged} />
        };

        this.isSplitterEnabled = isFeatureFlagEnabled(this.context.pageContext, AdminHubComponent.AdminHubSplitterEnabledFeatureFlag, false);
        this.isSearchFlagEnabled = isFeatureEnabled(this.context.pageContext, AdminHubComponent.AdminHubSettingsSearchEnabledFeatureId, false);
    }

    private onSelectedTabChanged = (newTabId: string): void => {
        if (this.selectedTabId.value !== newTabId) {
            this.selectedTabId.value = newTabId;
            this.updatePageTitleAndBreadcrumb(newTabId);
        }
    };

    private peek = () => {
        return this.stackPanel.peek();
    };

    private pop = () => {
        return this.stackPanel.pop();
    };

    private preProcessContributedTab(tab: IVssContributedTab | ILegacyContributedTab, groupId: string) {
        const legacyTab: ILegacyContributedTab = tab as ILegacyContributedTab;
        if (!legacyTab.id && legacyTab.itemKey) {
            legacyTab.id = legacyTab.itemKey;
        }

        if (!legacyTab.groupId) {
            legacyTab.groupId = groupId;
        }

        if (this.props.displayTabIcons !== true) {
            legacyTab.iconProps = undefined;
        }
    }

    private push = (item: IStackPanelItem) => {
        return this.stackPanel.push(item);
    };

    private updatePageTitleAndBreadcrumb = (newTabId: string) => {
        const contributedItem = this.contributionMap[newTabId];

        if (contributedItem && contributedItem.name) {
            const navigationService = this.context.pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
            let title = `${AdminResources.Settings} · ${contributedItem.name}`;
            if (this.entityName) {
                title += ` (${this.entityName})`;
            }
            navigationService.setPageTitle(title);

            this.adminBreadcrumbServie.setSelectedPivotBreadcrumb(
                ObservableLike.getValue(contributedItem.name),
                contributedItem.url && ObservableLike.getValue(contributedItem.url)
            );
        }
    };

    private renderTitle = (header: string): JSX.Element => {
        return <div className="bolt-tablist-title title-m flex-noshrink">{header}</div>;
    };

    private renderSubTitle = (header: string): JSX.Element => {
        return <div className="bolt-tablist-subtitle secondary-text flex-noshrink text-ellipsis">{header}</div>;
    };
}

Components.add("adminHub", AdminHubComponent);
